/* Product-scoped selection, quantity and price synchronization. No analytics data is sent. */
(() => {
  if (window.NurturadePurchase) return;
  const controllers = new WeakMap();
  const strings = () => window.variantStrings || {};

  class PurchaseController {
    constructor(root, data) {
      this.root = root;
      this.data = data;
      this.form = document.getElementById(data.formId);
      if (!this.form || !root.contains(this.form)) return;
      this.idInput = this.form.querySelector('[name="id"]');
      this.picker = root.querySelector('[data-product-options]');
      this.box = root.querySelector('.pt-price-qty-box');
      this.updating = false;
      this.variant = this.find(this.idInput?.value);
      this.selected = [...(this.variant?.options || [])];
      this.quantity = this.getQuantityInput();
      if (!this.quantity && this.box) {
        this.quantity = document.createElement('input');
        this.quantity.type = 'hidden';
        this.quantity.name = 'quantity';
        this.quantity.value = '1';
        this.form.appendChild(this.quantity);
      }
      if (this.box) {
        // The original quantity control remains the submitted input; show one control only.
        const originalGroup = root.querySelector('.product-form__quantity');
        if (originalGroup && !originalGroup.contains(this.box)) originalGroup.hidden = true;
      }
      if (this.picker) {
        // Preserve custom option cards and supply any otherwise omitted product options.
        (data.optionNames || []).forEach((name, index) => {
          if (this.picker.querySelector(`[data-option-position="${index + 1}"]`)) return;
          const label = document.createElement('label');
          label.textContent = name;
          const select = document.createElement('select');
          select.dataset.nurturadeOptionSelect = '';
          select.dataset.optionPosition = index + 1;
          [...new Set(data.variants.map(v => v.options[index]))].forEach(value => {
            const option = document.createElement('option');
            option.value = value; option.textContent = value; select.appendChild(option);
          });
          label.appendChild(select); this.picker.appendChild(label);
        });
        this.status = document.createElement('p');
        this.status.className = 'nurturade-selection-status';
        this.status.setAttribute('role', 'status');
        this.status.id = `nurturade-selection-${root.dataset.section}`;
        this.picker.appendChild(this.status);
      }
      root.addEventListener('click', event => this.onClick(event));
      root.addEventListener('change', event => this.onChange(event));
      root.addEventListener('input', event => {
        if (event.target.matches('[data-qty-input]') && /^\d+$/.test(event.target.value)) {
          this.setQuantity(event.target.value);
        }
      });
      // Capture before product-form's submit listener: the last selected variant must be valid.
      this.form.addEventListener('submit', event => {
        if (this.picker && (!this.variant || !this.variant.available)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.status?.focus();
          return;
        }
        if (this.box) this.setQuantity(this.box.querySelector('[data-qty-input]').value);
        const quantity = this.getQuantityInput();
        if (quantity && !quantity.checkValidity()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          quantity.reportValidity();
        }
      }, true);
      if (this.picker) this.applySelection(false);
      else this.renderPrices();
      if (this.box) this.setQuantity(this.quantity?.value || 1, false);
      this.setupSticky();
    }
    find(id) { return this.data.variants.find(v => String(v.id) === String(id)) || null; }
    getQuantityInput() {
      return Array.from(this.form.elements).find(el => el.name === 'quantity' && el.tagName === 'INPUT');
    }
    format(cents) {
      return new Intl.NumberFormat(this.data.locale || 'de', {
        style: 'currency', currency: this.data.currency || 'CHF'
      }).format(Number(cents || 0) / 100);
    }
    onClick(event) {
      const option = event.target.closest('.taste-button, .size-variant-box, [data-nurturade-option]');
      if (option && this.picker?.contains(option)) {
        event.preventDefault();
        if (option.disabled) return;
        this.selected[Number(option.dataset.optionPosition) - 1] = option.dataset.optionValue;
        this.applySelection(true);
      }
      if (this.box && this.box.contains(event.target)) {
        if (event.target.closest('[data-qty-minus]')) this.setQuantity(Number(this.quantity.value) - Number(this.quantity.step || 1));
        if (event.target.closest('[data-qty-plus]')) this.setQuantity(Number(this.quantity.value) + Number(this.quantity.step || 1));
      }
      const stickyButton = event.target.closest('.sticky-atc-trigger');
      if (stickyButton && this.root.contains(stickyButton)) {
        event.preventDefault();
        const button = this.form.querySelector('[type="submit"]');
        if (button && !button.disabled && button.getAttribute('aria-disabled') !== 'true') button.click();
      }
    }
    onChange(event) {
      if (this.updating) return;
      if (event.target === this.idInput) {
        this.variant = this.find(this.idInput.value);
        this.renderPrices();
      }
      if (event.target.matches('[data-nurturade-option-select]') && this.picker?.contains(event.target)) {
        this.selected[Number(event.target.dataset.optionPosition) - 1] = event.target.value;
        this.applySelection(true);
      }
      if (this.box && (event.target === this.quantity || event.target.matches('[data-qty-input]'))) {
        this.setQuantity(event.target.value, event.target !== this.quantity);
      }
    }
    applySelection(updateURL) {
      this.variant = this.data.variants.find(v => v.options.every((value, index) => String(value) === String(this.selected[index]))) || null;
      const variant = this.variant;
      const valid = Boolean(variant?.available);
      this.updating = true;
      this.idInput.value = variant ? String(variant.id) : '';
      this.idInput.disabled = !variant;
      this.picker.dataset.currentVariant = variant ? String(variant.id) : '';
      this.picker.querySelectorAll('.taste-button, .size-variant-box').forEach(button => {
        const index = Number(button.dataset.optionPosition) - 1;
        const active = String(this.selected[index]) === button.dataset.optionValue;
        const candidates = this.data.variants.filter(v => String(v.options[index]) === button.dataset.optionValue);
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', String(active));
        // Only disable values unavailable across all combinations, avoiding selection dead ends.
        button.disabled = !candidates.some(v => v.available);
        button.setAttribute('aria-describedby', this.status.id);
      });
      this.picker.querySelectorAll('[data-nurturade-option-select]').forEach(select => {
        select.value = this.selected[Number(select.dataset.optionPosition) - 1] || '';
      });
      const unavailable = variant ? strings().soldOut : strings().unavailable;
      this.status.textContent = this.selected.join(' · ') + (valid ? '' : ` — ${unavailable || 'Unavailable'}`);
      this.status.tabIndex = -1;
      const productForm = this.form.closest('product-form');
      productForm?.handleErrorMessage?.();
      const button = this.form.querySelector('[type="submit"]');
      if (button) {
        button.disabled = !valid;
        const span = button.querySelector('span');
        if (span) span.textContent = valid ? strings().addToCart : unavailable;
      }
      if (variant && this.quantity) {
        this.quantity.min = variant.min || 1;
        this.quantity.dataset.min = variant.min || 1;
        this.quantity.step = variant.step || 1;
        if (variant.max != null) this.quantity.max = variant.max;
        else this.quantity.removeAttribute('max');
      }
      this.updating = false;
      if (this.quantity) this.setQuantity(this.quantity.value, false);
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.renderPrices();
      if (updateURL && this.root.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        if (variant) url.searchParams.set('variant', variant.id);
        else url.searchParams.delete('variant');
        window.history.replaceState({}, '', url);
      }
      if (variant?.featured_media && updateURL) {
        const gallery = this.root.querySelector('media-gallery');
        const media = gallery?.querySelector(`[data-media-id$="-${variant.featured_media}"]`);
        if (media && typeof gallery.setActiveMedia === 'function') gallery.setActiveMedia(media.dataset.mediaId, false);
      }
      this.root.dispatchEvent(new CustomEvent('nurturade:variant-change', {
        bubbles: true, detail: { variant, sectionId: this.root.dataset.section }
      }));
    }
    setQuantity(value, notify = true) {
      if (!this.quantity || this.updating) return;
      const min = Number(this.quantity.min || this.quantity.dataset.min) || 1;
      const step = Number(this.quantity.step) || 1;
      const max = this.quantity.max === '' ? Infinity : Number(this.quantity.max);
      let qty = Number(value);
      if (!Number.isFinite(qty)) qty = min;
      qty = Math.max(min, min + Math.floor((qty - min) / step) * step);
      if (Number.isFinite(max)) qty = Math.min(qty, min + Math.floor((max - min) / step) * step);
      qty = Math.max(min, qty);
      const changed = String(qty) !== this.quantity.value;
      this.updating = true;
      this.quantity.value = qty;
      if (this.box) {
        const input = this.box.querySelector('[data-qty-input]');
        input.value = qty;
        input.min = min; input.step = step;
        if (Number.isFinite(max)) input.max = max; else input.removeAttribute('max');
        this.box.querySelector('[data-qty-minus]').disabled = qty <= min;
        this.box.querySelector('[data-qty-plus]').disabled = qty + step > max;
      }
      if (changed && notify) this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      this.updating = false;
      this.renderPrices();
    }
    renderPrices() {
      const variant = this.variant;
      this.root.querySelectorAll('.custom-price-wrapper').forEach(wrapper => {
        wrapper.hidden = !variant;
        if (!variant) return;
        const current = wrapper.querySelector('.current-price');
        if (current) current.textContent = this.format(variant.price);
        const sale = Number(variant.compare_at_price) > variant.price;
        const compare = wrapper.querySelector('.compare-price');
        const badge = wrapper.querySelector('.discount-badge');
        if (compare) { compare.textContent = this.format(variant.compare_at_price); compare.style.display = sale ? 'inline' : 'none'; }
        if (badge) { badge.textContent = sale ? `-${Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price)}%` : ''; badge.style.display = sale ? 'inline-flex' : 'none'; }
      });
      if (this.box) {
        this.box.querySelector('[data-total-price]').textContent = variant ? this.format(variant.price * Number(this.quantity?.value || 1)) : '—';
      }
      this.root.querySelectorAll('.sticky-atc-price').forEach(price => {
        price.textContent = variant ? this.format(variant.price) : '—';
      });
      this.root.querySelectorAll('.sticky-atc-active-variant').forEach(label => {
        label.textContent = variant && variant.options[0] !== 'Default Title' ? variant.options.join(' / ') : '';
      });
      if (variant?.featured_image) this.root.querySelectorAll('.sticky-atc-image img').forEach(img => {
        img.src = variant.featured_image; img.removeAttribute('srcset');
      });
      this.syncSticky();
    }
    syncSticky() {
      const main = this.form.querySelector('[type="submit"]');
      if (!main) return;
      this.root.querySelectorAll('.sticky-atc-trigger').forEach(button => {
        button.disabled = main.disabled || main.getAttribute('aria-disabled') === 'true';
        button.classList.toggle('loading', main.classList.contains('loading'));
        const text = button.querySelector('span');
        if (text) text.textContent = main.querySelector('span')?.textContent || strings().addToCart;
      });
    }
    setupSticky() {
      const sticky = this.root.querySelector('.sticky-atc');
      const button = this.form.querySelector('[type="submit"]');
      if (!sticky || !button) return;
      this.buttonObserver = new MutationObserver(() => this.syncSticky());
      this.buttonObserver.observe(button, { attributes: true, childList: true, subtree: true, characterData: true });
      const visibility = () => {
        const rect = button.getBoundingClientRect();
        const show = sticky.classList.contains('always-visible') || rect.bottom < 0;
        sticky.classList.toggle('show', show);
        sticky.style.pointerEvents = show ? 'auto' : 'none';
      };
      let frame = false;
      this.onScroll = () => {
        if (frame) return;
        frame = true;
        requestAnimationFrame(() => { frame = false; visibility(); });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      visibility(); this.syncSticky();
    }
    destroy() {
      this.buttonObserver?.disconnect();
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
    }
  }
  function init(container = document) {
    const roots = container.matches?.('product-info') ? [container] : container.querySelectorAll('product-info');
    roots.forEach(root => {
      if (controllers.has(root)) return;
      const node = root.querySelector('[data-nurturade-product]');
      if (!node) return;
      try {
        const data = JSON.parse(node.textContent);
        if (!document.getElementById(data.formId)) return;
        controllers.set(root, new PurchaseController(root, data));
      } catch (error) { console.error('Product controls could not be initialized', error); }
    });
  }
  window.NurturadePurchase = { init };
  function start() {
    init();
    if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      document.querySelectorAll('product-info').forEach(root => {
        if (String(root.dataset.section) !== String(data.sectionId)) return;
        const controller = controllers.get(root);
        if (!controller || controller.picker) return;
        controller.variant = controller.find(data.variant?.id);
        controller.renderPrices();
      });
    });
    const observer = new MutationObserver(records => {
      records.forEach(record => {
        record.removedNodes.forEach(node => {
          if (node.nodeType !== 1 || node.isConnected) return;
          const roots = node.matches('product-info') ? [node] : node.querySelectorAll('product-info');
          roots.forEach(root => { controllers.get(root)?.destroy(); controllers.delete(root); });
        });
        record.addedNodes.forEach(node => {
          if (node.nodeType === 1 && (node.matches('product-info') || node.querySelector('product-info'))) init(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
